import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { MapPin, Calendar, Maximize2, Eye, ChevronLeft, ChevronRight, X } from 'lucide-react';
import api from '../lib/api';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const toAbsolute = (url) => (url && url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url);

const ProjectsSection = () => {
  const [filter, setFilter] = useState('Tous');
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(true);
  const [loadTimeout, setLoadTimeout] = useState(false);
  const [projects, setProjects] = useState([]);
  const categories = ['Tous', 'Gros Œuvre', 'Second Œuvre', 'Promotion Immobilière'];

  useEffect(() => {
    let alive = true;
    api.get('/projects').then(({ data }) => {
      if (alive) {
        // Map backend shape: image (cover) = first of images
        const mapped = data.map((p) => ({
          ...p,
          image: toAbsolute(p.images?.[0]),
          images: (p.images || []).map(toAbsolute),
        }));
        setProjects(mapped);
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const filteredProjects = (filter === 'Tous'
    ? projects
    : projects.filter(p => p.category === filter))
    .sort((a, b) => {
      // Tri : du plus récent au plus ancien (année puis ordre d'ajout)
      const yearA = parseInt(a.year) || 0;
      const yearB = parseInt(b.year) || 0;
      if (yearB !== yearA) return yearB - yearA;
      return (b.order || 0) - (a.order || 0);
    });

  const handleProjectClick = (project) => {
    // Ouvrir le modal immédiatement pour feedback visuel
    setSelectedProject(project);
    setCurrentImageIndex(0);
    setImageLoading(true);
    setLoadTimeout(false);
    
    // Si l'image prend plus de 3 secondes, afficher un message
    const timeoutId = setTimeout(() => {
      setLoadTimeout(true);
    }, 3000);
    
    // Nettoyer le timeout
    return () => clearTimeout(timeoutId);
  };

  const handleCloseModal = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
    setImageLoading(true);
  };

  const nextImage = () => {
    if (selectedProject && selectedProject.images) {
      setImageLoading(true);
      setCurrentImageIndex((prev) => 
        prev === selectedProject.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject && selectedProject.images) {
      setImageLoading(true);
      setCurrentImageIndex((prev) => 
        prev === 0 ? selectedProject.images.length - 1 : prev - 1
      );
    }
  };

  // Helper function to detect if media is video
  const isVideo = (url) => {
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.ogg', '.MOV', '.MP4'];
    return videoExtensions.some(ext => url?.toLowerCase().endsWith(ext.toLowerCase()));
  };

  return (
    <section id="projects" className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-0 w-96 h-96 bg-yellow-100 rounded-full blur-3xl opacity-20"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <span className="bg-gradient-to-r from-yellow-500 to-yellow-400 text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg">
            Nos Réalisations
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
            Des projets qui témoignent
            <span className="block mt-2 bg-gradient-to-r from-yellow-500 to-yellow-400 bg-clip-text text-transparent">
              de notre savoir-faire
            </span>
          </h2>
        </div>

        {/* Filter buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all shadow-md hover:shadow-xl ${
                filter === category
                  ? 'bg-gradient-to-r from-yellow-500 to-yellow-400 text-white scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200 hover:border-blue-300'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Projects grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {filteredProjects.map((project) => (
            <Card
              key={project.id}
              className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-yellow-200 cursor-pointer bg-white"
              onClick={() => handleProjectClick(project)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleProjectClick(project);
                }
              }}
            >
              <div className="relative h-72 overflow-hidden bg-gray-100">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-yellow-500/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="bg-white rounded-full p-4 transform scale-0 group-hover:scale-100 transition-transform">
                    <Eye className="text-yellow-600" size={28} />
                  </div>
                </div>
                
                <Badge
                  className={`absolute top-4 right-4 shadow-lg ${
                    project.status === 'Terminé'
                      ? 'bg-green-500'
                      : project.status === 'En cours'
                      ? 'bg-yellow-500'
                      : 'bg-gradient-to-r from-yellow-500 to-yellow-400'
                  } text-white border-0 px-4 py-1.5`}
                >
                  {project.status}
                </Badge>
                
                <div className="absolute bottom-4 left-4 right-4">
                  <Badge className="bg-white/95 backdrop-blur-sm text-gray-900 mb-2 border-0">
                    {project.category}
                  </Badge>
                  <h3 className="text-2xl font-bold text-white drop-shadow-lg">{project.title}</h3>
                </div>
              </div>
              
              <CardContent className="p-8 space-y-4">
                <p className="text-gray-600 leading-relaxed">{project.description}</p>
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <MapPin size={16} className="text-yellow-600" />
                    <span className="truncate">{project.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Calendar size={16} className="text-yellow-600" />
                    <span>{project.year}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Maximize2 size={16} className="text-yellow-600" />
                    <span>{project.surface}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Modal pour afficher l'image en grand avec carrousel */}
        <Dialog open={!!selectedProject} onOpenChange={handleCloseModal}>
          <DialogContent className="max-w-6xl max-h-[95vh] p-0 overflow-y-auto relative !fixed !left-1/2 !top-1/2 !-translate-x-1/2 !-translate-y-1/2 !duration-300 !transition-all">
            {selectedProject && (() => {
              const projectImages = selectedProject?.images || [selectedProject?.image];
              return (
              <div className="relative">
                {/* Carrousel d'images et vidéos */}
                <div className="relative h-[50vh] md:h-[70vh] bg-black group">
                  {isVideo(projectImages[currentImageIndex]) ? (
                    <video
                      src={projectImages[currentImageIndex]}
                      controls
                      className="w-full h-full object-contain"
                      key={currentImageIndex}
                    >
                      Votre navigateur ne supporte pas la lecture de vidéos.
                    </video>
                  ) : (
                    <>
                      {imageLoading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black px-4">
                          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-400 mb-4"></div>
                          <p className="text-white text-base font-semibold mb-2">Chargement de l'image...</p>
                          {loadTimeout && (
                            <p className="text-yellow-400 text-sm text-center">
                              Connexion lente détectée. L'image est en cours de chargement...
                            </p>
                          )}
                        </div>
                      )}
                      <img
                        src={projectImages[currentImageIndex]}
                        alt={`${selectedProject.title} - Photo ${currentImageIndex + 1}`}
                        className={`w-full h-full object-contain transition-opacity duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                        onLoad={() => setImageLoading(false)}
                        onError={() => setImageLoading(false)}
                        loading="eager"
                        decoding="async"
                      />
                    </>
                  )}
                  
                  {/* Navigation si plusieurs images */}
                  {projectImages.length > 1 && (
                    <>
                      {/* Bouton précédent */}
                      <Button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-900 rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      >
                        <ChevronLeft size={24} />
                      </Button>
                      
                      {/* Bouton suivant */}
                      <Button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-900 rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      >
                        <ChevronRight size={24} />
                      </Button>
                      
                      {/* Indicateur de position - masqué si une seule image */}
                      {projectImages.length > 1 && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-medium">
                          {currentImageIndex + 1} / {projectImages.length}
                        </div>
                      )}
                      
                      {/* Points de navigation */}
                      <div className="absolute bottom-4 right-4 flex gap-2">
                        {projectImages.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              index === currentImageIndex 
                                ? 'bg-yellow-500 w-8' 
                                : 'bg-white/50 hover:bg-white/80'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
                
                {/* Détails du projet */}
                <div className="p-8 bg-white">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <Badge className={`mb-3 ${
                        selectedProject.status === 'Terminé'
                          ? 'bg-green-500'
                          : selectedProject.status === 'En cours'
                          ? 'bg-yellow-500'
                          : 'bg-gradient-to-r from-yellow-500 to-yellow-400'
                      } text-white border-0`}>
                        {selectedProject.status}
                      </Badge>
                      <h3 className="text-3xl font-bold text-gray-900 mb-2">{selectedProject.title}</h3>
                      <Badge className="bg-yellow-50 text-yellow-700 border-0">
                        {selectedProject.category}
                      </Badge>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 leading-relaxed mb-6 text-lg">
                    {selectedProject.description}
                  </p>
                  
                  <div className="grid grid-cols-3 gap-6 pt-6 border-t border-gray-200">
                    <div className="flex items-center gap-3">
                      <MapPin className="text-yellow-500" size={24} />
                      <div>
                        <div className="text-sm text-gray-500">Localisation</div>
                        <div className="font-semibold text-gray-900">{selectedProject.location}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="text-yellow-500" size={24} />
                      <div>
                        <div className="text-sm text-gray-500">Année</div>
                        <div className="font-semibold text-gray-900">{selectedProject.year}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Maximize2 className="text-yellow-500" size={24} />
                      <div>
                        <div className="text-sm text-gray-500">Surface</div>
                        <div className="font-semibold text-gray-900">{selectedProject.surface}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default ProjectsSection;
